// React
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Libraries
import Joi from "joi";
import PropTypes from "prop-types";
import { Form, Checkbox, Button, Divider } from "semantic-ui-react";
import { isEmpty } from "lodash";

// Redux
import { connect } from "react-redux";
import { login } from "@Actions/authentication";

// Components
import Input from "@Components/Input";
import Text from "@Components/Text";

// Utils
import { getValidationErrors } from "@Utils/functionality";
import { buildAuthPath } from "@Utils";

// Styles
import styles from "./styles.module.css";

const Login = ({ dispatch }) => {
	const navigate = useNavigate();
	const { companyName = "", token = "" } = useParams();
	const defaultErrors = { userName: "", password: "" };
	const [userName, setUserName] = useState("");
	const [password, setPassword] = useState("");
	const [selected, setSelected] = useState(false);
	const [errors, setErrors] = useState(defaultErrors);
	const schema = Joi.object().keys({
		userName: Joi.string()
			.min(4)
			.required()
			.custom((value, telep) => {
				if (/^\d+$/.test(value) && !value.startsWith("1")) {
					return telep.message("Debes agregar el 1 delante de tu teléfono para acceder");
				}
				return value;
			})
			.messages({
				"string.min": "Usuario debe tener mínimo 4 caracteres",
				"string.empty": "Usuario es un campo requerido",
			}),
		password: Joi.string().min(4).required().messages({
			"string.min": "Contraseña debe tener mínimo 4 caracteres",
			"string.empty": "Contraseña es un campo requerido",
		}),
	});

	useEffect(() => {
		const storedData = localStorage.getItem("credentials");
		if (storedData) {
			const credentials = JSON.parse(storedData);
			setUserName(credentials.userName);
			setPassword(credentials.password);
			setSelected(credentials.selected);
		}
	}, []);

	const cleanupForm = () => {
		setUserName("");
		setPassword("");
		setSelected(false);
		setErrors(defaultErrors);
	};

	const handleInput = (e) => {
		const { value, name } = e.target;
		setErrors(defaultErrors);
		switch (name) {
			case "userName":
				setUserName(value);
				break;
			case "password":
				setPassword(value);
				break;
			default:
				break;
		}
	};

	const handleLogin = () => {
		const validation = schema.validate({ userName, password }, { abortEarly: false });
		if (validation.error) {
			setErrors(getValidationErrors(validation, defaultErrors));
		} else {
			dispatch(login(userName, password, selected, cleanupForm, navigate));
		}
	};

	return (
		<>
			<Form>
				{token && companyName && (
					<Input className={styles.inputDisabled} icon="home" disabled label="Compañía" required value={companyName} />
				)}
				<Input
					className={styles.input}
					icon="user"
					label="Usuario"
					required
					error={!isEmpty(errors.userName) && { content: errors.userName, pointing: "above" }}
					placeholder="Email / Teléfono"
					name="userName"
					value={userName}
					onChange={handleInput}
					onKeyPress={handleLogin}
				/>
				<Input
					isPassword
					className={styles.input}
					label="Contraseña"
					required
					autoComplete="off"
					error={!isEmpty(errors.password) && { content: errors.password, pointing: "above" }}
					name="password"
					value={password}
					onChange={handleInput}
					onKeyPress={handleLogin}
				/>
			</Form>
			<Checkbox
				className={styles.checkBox}
				label="Recordarme"
				checked={selected}
				onChange={() => {
					return setSelected(!selected);
				}}
			/>
			<Button className={styles.button} size="medium" fluid onClick={handleLogin}>
				Iniciar Sesión
			</Button>
			<Text
				className={styles.buttonLink}
				size="medium"
				fluid
				onClick={() => {
					navigate(buildAuthPath(`/auth/register`, companyName, token));
				}}>
				¿No tienes una cuenta? Registrate
			</Text>

			<Divider />
			<Text
				className={styles.buttonLink}
				size="medium"
				fluid
				onClick={() => {
					navigate(buildAuthPath(`/auth/forgotPassword`, companyName, token));
				}}>
				¿Has olvidado la contraseña?
			</Text>
		</>
	);
};

Login.propTypes = {
	dispatch: PropTypes.func,
};

export default connect()(Login);
